import { QuestionFormCheckbox } from './components/QuestionFormCheckbox';
import { QuestionFormDate } from './components/QuestionFormDate';
import { QuestionFormEntries } from './components/QuestionFormEntries';
import { QuestionFormRadio } from './components/QuestionFormRadio';
import { QuestionFormSelect } from './components/QuestionFormSelect';
import { QuestionFormInputChips } from './components/QuestionFormInputChips';
import { QuestionFormSensitive } from './components/QuestionFormSensitive';
import { QuestionFormText } from './components/QuestionFormText';
import { QuestionFormTextarea } from './components/QuestionFormTextarea';
import { QuestionFormUpload } from './components/QuestionFormUpload';
import {
  PREDEFINED_NOT_APPLICABLE,
  PREDEFINED_MARKED_COMPLETED,
  PREDEFINED_PROVIDED_ELSEWHERE,
} from '@/_constants/questions';
import { QuestionNotApplicable } from './components/QuestionNotApplicable';
import { QuestionAnswerLater } from './components/QuestionAnswerLater';
import { QuestionProvidedElsewhere } from './components/QuestionProvidedElsewhere';
import type { FC } from 'react';
import type { QuestionFormSwitchProps } from './QuestionFormSwitch.types';
import { QuestionMarkedCompleted } from './components/QuestionMarkedCompleted';

export const QuestionFormSwitch: FC<QuestionFormSwitchProps> = ({
  question,
  mode,
  isDisabled,
}) => {
  const getQuestionForm = () => {
    if (question.answer === PREDEFINED_MARKED_COMPLETED) {
      return <QuestionMarkedCompleted />;
    }

    if (question.answer === PREDEFINED_NOT_APPLICABLE) {
      return <QuestionNotApplicable />;
    }

    if (question.answer === PREDEFINED_PROVIDED_ELSEWHERE) {
      return <QuestionProvidedElsewhere />;
    }

    if (question.answer_by) {
      return <QuestionAnswerLater question={question} />;
    }

    switch (question.question_type) {
      case 'boolean':
        return (
          <QuestionFormRadio
            question={question}
            mode={mode}
            isDisabled={isDisabled}
          />
        );
      case 'upload':
        return (
          <QuestionFormUpload
            question={question}
            mode={mode}
            isDisabled={isDisabled}
          />
        );
      case 'multiple':
        // Use InputChips for multiple selection with more than 10 options
        if (question.options && question.options.length > 20) {
          return (
            <QuestionFormInputChips
              question={question}
              mode={mode}
              isDisabled={isDisabled}
            />
          );
        }
        return (
          <QuestionFormCheckbox
            question={question}
            mode={mode}
            isDisabled={isDisabled}
          />
        );
      case 'select':
        // Use Select for single selection with more than 10 options
        if (question.options && question.options.length > 20) {
          return (
            <QuestionFormSelect
              question={question}
              mode={mode}
              isDisabled={isDisabled}
            />
          );
        }
        return (
          <QuestionFormRadio
            question={question}
            mode={mode}
            isDisabled={isDisabled}
          />
        );
      case 'entries':
        return (
          <QuestionFormEntries
            question={question}
            isDisabled={isDisabled}
            mode={mode}
          />
        );
      case 'password':
        return (
          <QuestionFormSensitive
            question={question}
            mode={mode}
            isDisabled={isDisabled}
          />
        );
      case 'date':
        return (
          <QuestionFormDate
            question={question}
            mode={mode}
            isDisabled={isDisabled}
          />
        );
      case 'textarea':
        return (
          <QuestionFormTextarea
            question={question}
            mode={mode}
            isDisabled={isDisabled}
          />
        );
      default:
        return (
          <QuestionFormText
            question={question}
            mode={mode}
            isDisabled={isDisabled}
          />
        );
    }
  };

  return getQuestionForm();
};
